import React, { ReactElement, useCallback, useState } from 'react';
import {
  Col, Heading, Link, Loader, Row,
} from 'idsk-react-components';
import { Message, SearchBox } from 'slovlex-idsk-react-components';
import { useClient } from '../../context/ClientStatusContext';
import './home.scss';
import AktualitaListGrid from '../Aktuality/List/component/AktualitaListGrid';
import { getAktualitaList } from '../../client/aktualitaListClient';
import VybranePredpisy from './component/VybranePredpisy/VybranePredpisy';
import NajnovsiObsah from './component/NajnovsiObsah/NajnovsiObsah';
import PopularnyObsah from './component/PopularnyObsah/PopularnyObsah';
import routes from '../../routing/routes';
import { getSearchData } from '../../client/vyhladavanieDomovListClient';
import externalRoutes from '../../routing/externalRoutes';
import RssLink from '../../page/AktualitaListPage/RssLink';

const Home = (): ReactElement => {
  const {
    data: aktualitaPage,
    status: aktualitaPageStatus,
  } = useClient(
    () => getAktualitaList(3, 1),
  );

  const [dataVyhladavanie, setDataVyhladavanie] = useState<Props[]>([]);
  const [dataVyhladavanieStatus, setDataVyhladavanieStatus] = useState<{
    loading: boolean;
    responseCode?: number
  }>(
    { loading: false },
  );
  const [displaySearchError, setDisplaySearchError] = useState(true);
  const [displayAktualityError, setDisplayAktualityError] = useState(true);

  interface Props {
    nazov: string;
    iri: string;
  }

  const updateData = useCallback(async (
    value: string,
    dataProp: Props[],
    requestPath: string,
    path: string,
  ) => {
    const searchValueTrim = value.trim();
    if (searchValueTrim.length > 0) {
      try {
        const response = await getSearchData(
          searchValueTrim,
          `${requestPath}`,
          path,
        );
        const newData = response.data || [];
        setDataVyhladavanie(newData);
        setDataVyhladavanieStatus({
          loading: response.responseCode !== 200,
          responseCode: response.responseCode,
        });
        dataProp.splice(0, dataProp.length, ...newData);
      } catch (error) {
        setDataVyhladavanie([]);
        setDataVyhladavanieStatus({
          loading: false,
          responseCode: error,
        });
      }
    }
  }, []);

  const sekciaList = [{
    nazov: 'eZbierka',
    requestPath: '/vyhladavanie/predpisZbierky/navrhy?typ=predpisZbierky',
    searchPath: '/ezbierky/vyhladavanie-pravnych-predpisov',
    path: '/ezbierky/pravne-predpisy',
    searchParamName: 'hladanyVyraz',
  }, {
    nazov: 'eLegislatíva',
    requestPath: '/vyhladavanie/legislativnyMaterial/navrhy',
    searchPath: '/elegislativa/legislativne-procesy',
    path: '/elegislativa/legislativne-procesy/SK/',
    searchParamName: 'hladanyVyraz',
  },
  {
    nazov: 'Tezaurus',
    requestPath: '/vyhladavanie/tezaurus/navrhy',
    searchPath: '/tezaury/terminy',
    path: '/tezaury/terminy/',
    searchParamName: 'hladanyTermin',
  }, {
    nazov: 'Súdne rozhodnutia',
    requestPath: '/vyhladavanie/sudneRozhodnutie/navrhy',
    searchPath: '/sudne-rozhodnutia/judikaty',
    path: '/sudne-rozhodnutia/judikaty/',
    searchParamName: 'hladanyVyraz',
  }, {
    nazov: 'Európsky súd pre ĽP',
    requestPath: '/vyhladavanie/euSudneRozhodnutie/navrhy?typ=ESLP',
    searchPath: '/sudne-rozhodnutia/europsky-sud-lp',
    path: '/sudne-rozhodnutia/europsky-sud-lp/',
    searchParamName: 'hladanyVyraz',
  }, {
    nazov: 'Súdny dvor EÚ',
    requestPath: '/vyhladavanie/euSudneRozhodnutie/navrhy?typ=SDEU',
    searchPath: '/sudne-rozhodnutia/sudny-dvor-eu',
    path: '/sudne-rozhodnutia/sudny-dvor-eu/',
    searchParamName: 'hladanyVyraz',
  }];

  return (
    <div className="homepage">
      <Row>
        <Col width="two-thirds">
          <Heading size="l" className="homepage-main__heading">Slov-Lex</Heading>
          <Heading size="m" className="homepage-second__heading">právny a informačný
            portál
          </Heading>
          <p>Vyhľadávanie v sekciách Slov-Lex</p>
          <Loader loading={dataVyhladavanieStatus.loading}>
            {(dataVyhladavanieStatus.responseCode && dataVyhladavanieStatus.responseCode > 399)
              ? (
                <>
                  {displaySearchError && (
                    <Message
                      type="error"
                      headingSize="m"
                      onClear={() => setDisplaySearchError(false)}
                    >
                      Nepodarilo sa načítať záznamy.
                    </Message>
                  )}
                </>
              )
              : (
                <>
                  <Row>
                    <SearchBox
                      className="margin-search-box"
                      sekciaList={sekciaList}
                      updateData={updateData}
                      data={dataVyhladavanie}
                    />
                  </Row>
                </>
              )}
          </Loader>
        </Col>
        <Col width="one-third"> <PopularnyObsah /></Col>
      </Row>
      <NajnovsiObsah />
      <div className="homepage-aktuality-wrapper">
        <div className="homepage-aktuality">
          <Heading className="homepage-aktuality__heading" size="l">Aktuality</Heading>
          <RssLink to={externalRoutes.rss.aktuality} />
          <Loader loading={aktualitaPageStatus.loading}>
            {(aktualitaPageStatus.responseCode && aktualitaPageStatus.responseCode > 399)
              ? (
                <>
                  {displayAktualityError && (
                    <Message
                      type="error"
                      headingSize="m"
                      onClear={() => setDisplayAktualityError(false)}
                    >
                      Nepodarilo sa načítať aktuality.
                    </Message>
                  )}
                </>
              )
              : (
                <>
                  <AktualitaListGrid aktualitaList={aktualitaPage?.results} />
                  <Row>
                    <Col className="homepage-aktuality__link">
                      <Link to={routes.aktuality.to}>Staršie aktuality</Link>
                    </Col>
                  </Row>
                </>
              )}
          </Loader>
        </div>
      </div>
      <VybranePredpisy />
    </div>
  );
};

export default Home;
